import { 
    collection, 
    addDoc, 
    getDocs, 
    query, 
    orderBy, 
    limit, 
    where,
    updateDoc,
    doc,
    deleteDoc,
    serverTimestamp 
  } from 'firebase/firestore';
  import { db } from '../firebase';
  
  const CHANGELOG_COLLECTION = 'changelog';
  
  export const changelogService = {
    // Add a new changelog entry
    async addChangelogEntry(changelogEntry) {
      try {
        const changelogData = {
          ...changelogEntry,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, CHANGELOG_COLLECTION), changelogData);
        console.log('Changelog entry added with ID: ', docRef.id);
        return docRef.id;
      } catch (error) {
        console.error('Error adding changelog entry: ', error);
        throw error;
      }
    },
  
    // Get all changelog entries (ordered by date, newest first)
    async getAllChangelogEntries() {
      try {
        const changelogQuery = query(
          collection(db, CHANGELOG_COLLECTION),
          orderBy('date', 'desc')
        );
        
        const querySnapshot = await getDocs(changelogQuery);
        const changelogEntries = [];
        
        querySnapshot.forEach((doc) => {
          changelogEntries.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        return changelogEntries;
      } catch (error) {
        console.error('Error fetching changelog entries: ', error);
        throw error;
      }
    },
  
    // Get latest changelog entries (for home screen)
    async getLatestChangelogEntries(limitCount = 5) {
      try {
        const changelogQuery = query(
          collection(db, CHANGELOG_COLLECTION),
          orderBy('date', 'desc'),
          limit(limitCount)
        );
        
        const querySnapshot = await getDocs(changelogQuery);
        const changelogEntries = [];
        
        querySnapshot.forEach((doc) => {
          changelogEntries.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        return changelogEntries;
      } catch (error) {
        console.error('Error fetching latest changelog entries: ', error);
        throw error;
      }
    },
  
    // Get changelog entries by type (feature, improvement, fix)
    async getChangelogEntriesByType(type) {
      try {
        const changelogQuery = query(
          collection(db, CHANGELOG_COLLECTION),
          where('type', '==', type),
          orderBy('date', 'desc')
        );
        
        const querySnapshot = await getDocs(changelogQuery);
        const changelogEntries = [];
        
        querySnapshot.forEach((doc) => {
          changelogEntries.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        return changelogEntries;
      } catch (error) {
        console.error('Error fetching changelog entries by type: ', error);
        throw error;
      }
    },
  
    // Update a changelog entry
    async updateChangelogEntry(entryId, updatedData) {
      try {
        const entryRef = doc(db, CHANGELOG_COLLECTION, entryId);
        const updateData = {
          ...updatedData,
          updatedAt: serverTimestamp()
        };
        
        await updateDoc(entryRef, updateData);
        console.log('Changelog entry updated successfully');
        return true;
      } catch (error) {
        console.error('Error updating changelog entry: ', error);
        throw error;
      }
    },
  
    // Delete a changelog entry
    async deleteChangelogEntry(entryId) {
      try {
        await deleteDoc(doc(db, CHANGELOG_COLLECTION, entryId));
        console.log('Changelog entry deleted successfully');
        return true;
      } catch (error) {
        console.error('Error deleting changelog entry: ', error);
        throw error;
      }
    }
  };
  
  // Helper function to create sample changelog data (for testing)
  export const createSampleChangelogData = async () => {
    const sampleEntries = [
      {
        version: "2.1.0",
        date: "2025-09-08",
        type: "feature",
        title: "New Schedule View",
        description: "Added calendar view for better schedule visualization and conflict detection."
      },
      {
        version: "2.0.5",
        date: "2025-09-05",
        type: "improvement",
        title: "Performance Enhancements",
        description: "Improved loading times and optimized team member data synchronization."
      },
      {
        version: "2.0.4",
        date: "2025-09-01",
        title: "Bug Fixes",
        type: "fix",
        description: "Fixed issue with rota assignments not saving properly in some browsers."
      },
      {
        version: "2.0.3",
        date: "2025-08-28",
        type: "feature",
        title: "Mobile Improvements",
        description: "Enhanced mobile experience with better navigation and touch interactions."
      }
    ];
  
    try {
      for (const entry of sampleEntries) {
        await changelogService.addChangelogEntry(entry);
      }
      console.log('Sample changelog data created successfully');
    } catch (error) {
      console.error('Error creating sample changelog data: ', error);
    }
  };